import { isGeoblockActive } from "./geoblock";

import type {
  GetMediaResponse,
  MediaDistributionType,
  MediaRight,
  MediaRightsMode,
} from "../api/MediaAPI.types";
import type { CountryGroup } from "../api/ProjectAPI.types";

export const MEDIA_DISTRIBUTION_TYPE_LIST: MediaDistributionType[] = [
  "storefront",
  "fast",
  "d2g",
];

export type RestrictionValue = Pick<
  MediaRight,
  | "geoblock_mode"
  | "countries"
  | "country_groups"
  | "temporal_restriction"
  | "begin_date"
  | "end_date"
  | "commercial_restriction"
  | "commercial_restriction_begin_date"
  | "commercial_restriction_end_date"
  | "views_count"
>;

export type SimpleRestrictionValue = Omit<RestrictionValue, "views_count">;

export function getSimpleRestrictionValue(
  item: GetMediaResponse,
): SimpleRestrictionValue {
  return {
    geoblock_mode: item.geoblock_mode,
    countries: (item.countries ?? []).map((country) => country.uuid),
    country_groups: (item.country_groups ?? []).map(
      (countryGroup) => countryGroup.uuid,
    ),
    temporal_restriction: item.temporal_restriction,
    begin_date: item.begin_date,
    end_date: item.end_date,
    commercial_restriction: item.commercial_restriction,
    commercial_restriction_begin_date: item.commercial_restriction_begin_date,
    commercial_restriction_end_date: item.commercial_restriction_end_date,
  };
}

export function applySimpleRestrictionPatch(
  item: GetMediaResponse,
  patch: Partial<SimpleRestrictionValue>,
): GetMediaResponse {
  const {
    countries,
    country_groups: countryGroups,
    begin_date: beginDate,
    ...restPatch
  } = patch;

  return {
    ...item,
    ...restPatch,
    ...(beginDate ? { begin_date: beginDate } : {}),
    ...(countries ? { countries: countries.map((uuid) => ({ uuid })) } : {}),
    ...(countryGroups
      ? { country_groups: countryGroups.map((uuid) => ({ uuid })) }
      : {}),
  };
}

// The back refuses a scope key that doesn't match the distribution type.
function getScopeFields(
  distributionType: MediaDistributionType,
  right?: MediaRight,
): Pick<MediaRight, "storefront_project_uuid" | "fast_channel_uuid"> {
  if (distributionType === "storefront") {
    return { storefront_project_uuid: right?.storefront_project_uuid ?? null };
  }
  if (distributionType === "fast") {
    return { fast_channel_uuid: right?.fast_channel_uuid ?? null };
  }
  return {};
}

export function normalizeMediaRight(right: MediaRight): MediaRight {
  const {
    uuid: rightUuid,
    storefront_project_uuid: storefrontProjectUuid,
    fast_channel_uuid: fastChannelUuid,
    distribution_type: distributionType,
    ...restrictionValue
  } = right;

  return {
    distribution_type: distributionType,
    ...getScopeFields(distributionType, right),
    ...restrictionValue,
  };
}

export function getMediaRightList(item: GetMediaResponse): MediaRight[] {
  const simpleValue = getSimpleRestrictionValue(item);

  return MEDIA_DISTRIBUTION_TYPE_LIST.map((distributionType) => {
    // ponytail: one block per usage in V1, extra stored blocks of a usage are dropped on save
    const storedRight = (item.rights ?? []).find(
      (right) => right.distribution_type === distributionType,
    );

    return storedRight
      ? normalizeMediaRight(storedRight)
      : {
          distribution_type: distributionType,
          ...getScopeFields(distributionType),
          ...simpleValue,
          views_count: null,
        };
  });
}

export function updateMediaRight(
  rightList: MediaRight[],
  distributionType: MediaDistributionType,
  patch: Partial<MediaRight>,
): MediaRight[] {
  return rightList.map((right) =>
    right.distribution_type === distributionType
      ? { ...right, ...patch }
      : right,
  );
}

export function setRightsMode(
  item: GetMediaResponse,
  mode: MediaRightsMode,
): GetMediaResponse {
  if (mode === "simple") return { ...item, rights_mode: "simple" };

  return { ...item, rights_mode: "advanced", rights: getMediaRightList(item) };
}

export function getMediaRightsPayload(item: GetMediaResponse): MediaRight[] {
  if (item.rights_mode === "advanced") return getMediaRightList(item);

  // Stored blocks are sent back: `rights: []` would delete them server-side.
  return (item.rights ?? []).map(normalizeMediaRight);
}

export function hasInvalidViewsCount(item: GetMediaResponse): boolean {
  if (item.rights_mode !== "advanced") return false;

  return (item.rights ?? []).some(
    (right) =>
      right.views_count !== null &&
      (!Number.isInteger(right.views_count) || right.views_count < 0),
  );
}

interface VisibleDistributionTypeParams {
  isFastOnly: boolean;
  isOttOnly: boolean;
  allowFast: boolean;
}

export function getVisibleDistributionTypeList({
  isFastOnly,
  isOttOnly,
  allowFast,
}: VisibleDistributionTypeParams): MediaDistributionType[] {
  return MEDIA_DISTRIBUTION_TYPE_LIST.filter((distributionType) => {
    if (distributionType === "storefront") return !isFastOnly;
    if (distributionType === "fast") return !isOttOnly && allowFast;
    return true;
  });
}

export function getRightCountryCount(
  right: Pick<MediaRight, "countries" | "country_groups">,
  countryGroupList: CountryGroup[],
): number {
  const countryUuidSet = new Set(right.countries);
  countryGroupList
    .filter((group) => right.country_groups.includes(group.uuid))
    .forEach((group) =>
      group.countries.forEach((country) => countryUuidSet.add(country.uuid)),
    );
  return countryUuidSet.size;
}

export function hasActiveRestriction(
  right: Pick<
    MediaRight,
    | "geoblock_mode"
    | "temporal_restriction"
    | "commercial_restriction"
    | "views_count"
  >,
): boolean {
  return (
    isGeoblockActive(right.geoblock_mode) ||
    right.temporal_restriction ||
    right.commercial_restriction ||
    right.views_count !== null
  );
}
